import { useMemo } from 'react'
import useSWR from 'swr'
import {
  WORKFLOW_REPORT_SKILL_SLUG,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import { skillsApi, workflowsApi } from '@/lib/api'

/**
 * The skill a slash command attaches — the copy we may show in the hover.
 *
 * The step's own `instructions` (JSON shape, SurveyJS types, output
 * contracts) stay internal. A `report` step with no `skill:` still uses
 * the house report skill.
 */
export function firstCommandSkillSlug(
  template: WorkflowTemplate | null | undefined,
): string | null {
  if (!template) {
    return null
  }

  for (const step of template.steps) {
    if (step.kind === 'prompt' && step.skill) {
      return step.skill
    }
    if (step.kind === 'report') {
      return step.skill ?? WORKFLOW_REPORT_SKILL_SLUG
    }
  }

  return null
}

/**
 * First readable paragraph of a skill body: skip `#` headings so the
 * hover starts on the Role prose, not the markdown outline.
 */
export function skillPreviewCopy(instructions: string): string {
  const kept: Array<string> = []
  for (const line of instructions.split('\n')) {
    if (line.trim().startsWith('#')) {
      continue
    }
    if (line.trim().length === 0 && kept.length === 0) {
      continue
    }
    if (line.trim().length === 0 && kept[kept.length - 1] === '') {
      continue
    }
    kept.push(line)
  }
  return kept.join('\n').trim()
}

/**
 * Skill bodies for slash-command tokens. Templates are only read to find
 * the skill slug; step prompts are never returned. A failed read leaves
 * the map empty so the caller keeps the catalogue fallback.
 */
export function useCommandDrivingCopies(
  tokens: Array<string>,
): Map<string, string> {
  const tokenKey = tokens.join('\n')
  const uniqueTokens = useMemo(() => {
    const seen = new Set<string>()
    const ordered: Array<string> = []
    if (tokenKey.length === 0) {
      return ordered
    }
    for (const token of tokenKey.split('\n')) {
      if (token.length === 0 || seen.has(token)) {
        continue
      }
      seen.add(token)
      ordered.push(token)
    }
    return ordered
  }, [tokenKey])

  const { data: templates } = useSWR(
    uniqueTokens.length > 0
      ? ['workflow-templates', 'command-driving', ...uniqueTokens]
      : null,
    async () => {
      return Promise.all(
        uniqueTokens.map(async (token) => {
          try {
            return await workflowsApi.getTemplate(token)
          } catch {
            return null
          }
        }),
      )
    },
  )

  const tokenToSlug = useMemo(() => {
    const mapped = new Map<string, string>()
    if (!templates) {
      return mapped
    }
    uniqueTokens.forEach((token, index) => {
      const slug = firstCommandSkillSlug(templates[index])
      if (slug) {
        mapped.set(token, slug)
      }
    })
    return mapped
  }, [templates, uniqueTokens])

  const skillSlugs = useMemo(
    () => Array.from(new Set(Array.from(tokenToSlug.values()))),
    [tokenToSlug],
  )

  const { data: skills } = useSWR(
    skillSlugs.length > 0
      ? ['skills', 'command-driving', ...skillSlugs]
      : null,
    async () => {
      try {
        const list = await skillsApi.list()
        const bySlug = new Map(list.map((summary) => [summary.slug, summary]))
        const resolved = await Promise.all(
          skillSlugs.map(async (slug) => {
            const summary = bySlug.get(slug)
            if (!summary) {
              return null
            }
            try {
              return await skillsApi.get(summary.token)
            } catch {
              return null
            }
          }),
        )
        return resolved
      } catch {
        return []
      }
    },
  )

  return useMemo(() => {
    const copies = new Map<string, string>()
    if (!skills || skills.length === 0) {
      return copies
    }

    const instructionsBySlug = new Map<string, string>()
    skillSlugs.forEach((slug, index) => {
      const skill = skills[index]
      if (!skill) {
        return
      }
      const copy = skillPreviewCopy(skill.instructions)
      if (copy.length > 0) {
        instructionsBySlug.set(slug, copy)
      }
    })

    tokenToSlug.forEach((slug, token) => {
      const copy = instructionsBySlug.get(slug)
      if (copy) {
        copies.set(token, copy)
      }
    })

    return copies
  }, [skills, skillSlugs, tokenToSlug])
}
